import React from 'react';
import { useLocation } from 'react-router-dom';
import { GenericFormPage } from '../../components/generic';
import { useTask, Task, TaskStatusOptionRecord, useTaskStatusesForSelect } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';
import { normalizeHexColor, parseStatusColorConfig } from '../../utils/statusColor';

const Form: React.FC = () => {
  const location = useLocation();
  const { taskStatuses } = useTaskStatusesForSelect();
  const taskStatusOptions = React.useMemo(
    () =>
      taskStatuses.map((status: TaskStatusOptionRecord) => {
        const color = normalizeHexColor(parseStatusColorConfig(status.ts_color).backgroundColor) ?? '#64748b';
        return {
          value: String(status.id),
          label: status.ts_descr || `Status #${status.id}`,
          icon: (
            <span
              className="inline-block h-2.5 w-2.5 rounded-full border border-black/10"
              style={{ backgroundColor: color }}
            />
          ),
        };
      }),
    [taskStatuses],
  );

  // Use apiEndpoint + labelField + valueField so options are fetched by the generic form (FieldWithApiOptions)
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'taskAssignedFrom') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Assigned From',
          placeholder: 'Select a user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'taskAssignedTo') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Asgn',
          placeholder: 'Select a user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'projectId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Project',
          placeholder: 'Select a project (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-projects',
          labelField: 'projectName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'supportTicketId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Support Ticket',
          placeholder: 'Select a support ticket (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/support-ticket',
          labelField: 'title',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'taskCategoryId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Task Category',
          placeholder: 'Select a category',
          options: [],
          apiEndpoint: '/p-crm-task-category',
          labelField: 'tc_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'taskTypeId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Task Type',
          placeholder: 'Select a type (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-task-type',
          labelField: 'tt_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'taskPriority') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Priority',
          placeholder: 'Select a priority (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/p-crm-priority',
          labelField: 'p_descr',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'taskStatus') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Task Status',
          placeholder: 'Select a status',
          options: taskStatusOptions,
        };
      }
      return field;
    });

    // Form data allows foreign keys as string for select components
    type FormTask = Omit<Task, 'clientId' | 'taskAssignedFrom' | 'taskAssignedTo' | 'projectId' | 'supportTicketId' | 'taskCategoryId' | 'taskTypeId' | 'taskPriority' | 'taskStatus'> & {
      clientId?: string | number;
      taskAssignedFrom?: string | number;
      taskAssignedTo?: string | number;
      projectId?: string | number;
      supportTicketId?: string | number;
      taskCategoryId?: string | number;
      taskTypeId?: string | number;
      taskPriority?: string | number;
      taskStatus?: string | number;
    };

    // Custom mapItemToFormData that converts foreign keys to string for select
    const customMapItemToFormData = (item: Task): Partial<FormTask> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        taskAssignedFrom: baseData.taskAssignedFrom ? String(baseData.taskAssignedFrom) : '',
        taskAssignedTo: baseData.taskAssignedTo ? String(baseData.taskAssignedTo) : '',
        projectId: baseData.projectId ? String(baseData.projectId) : '',
        supportTicketId: baseData.supportTicketId ? String(baseData.supportTicketId) : '',
        taskCategoryId: baseData.taskCategoryId ? String(baseData.taskCategoryId) : '',
        taskTypeId: baseData.taskTypeId ? String(baseData.taskTypeId) : '',
        taskPriority: baseData.taskPriority ? String(baseData.taskPriority) : '',
        taskStatus: baseData.taskStatus ? String(baseData.taskStatus) : '',
      };
    };

    // Custom getInitialFormData (merge support task defaults from location.state when present)
    const supportTaskDefaults = (location.state as { supportTaskDefaults?: Record<string, unknown> })?.supportTaskDefaults;
    const customGetInitialFormData = (): Partial<FormTask> => {
      const base: Partial<FormTask> = {
        ...getInitialFormData(),
        clientId: '',
        taskAssignedFrom: '',
        taskAssignedTo: '',
        projectId: '',
        supportTicketId: '',
        taskCategoryId: '',
        taskTypeId: '',
        taskPriority: '',
        taskStatus: '',
      };
      if (supportTaskDefaults && typeof supportTaskDefaults === 'object') {
        const d = supportTaskDefaults as Record<string, unknown>;
        // New IT Support Task: Start Datetime empty; Finish Date & Time = now
        return {
          ...base,
          ...supportTaskDefaults,
          taskAssignedFrom: d.taskAssignedFrom != null ? String(d.taskAssignedFrom) : base.taskAssignedFrom,
          taskAssignedTo: d.taskAssignedTo != null ? String(d.taskAssignedTo) : base.taskAssignedTo,
          taskCategoryId: d.taskCategoryId != null ? String(d.taskCategoryId) : base.taskCategoryId,
          taskStatus: d.taskStatus != null ? String(d.taskStatus) : base.taskStatus,
          taskDate: d.taskDate != null ? String(d.taskDate) : base.taskDate,
          taskStartDate: undefined,
          taskEndDate: new Date().toISOString(),
        };
      }
      return base;
    };

    // Helper function to convert string to number or undefined
    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') {
        return undefined;
      }
      return Number(value);
    };

    // Helper function to convert empty datetime strings to undefined
    const convertDateTime = (value: string | undefined): string | undefined => {
      if (value === undefined || value === null || String(value).trim() === '') {
        return undefined;
      }
      return value;
    };

    // Helper function to convert date-only strings to ISO-8601 datetime strings
    // Prisma DateTime fields require full ISO-8601 format, not just dates
    const convertDateToDateTime = (value: string | undefined): string | undefined => {
      if (value === undefined || value === null || String(value).trim() === '') {
        return undefined;
      }
      const dateStr = String(value).trim();
      
      // If it's already a full ISO-8601 datetime string (contains 'T' or 'Z'), return as is
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        return dateStr;
      }
      
      // If it's a date-only string (YYYY-MM-DD), convert to ISO-8601 at local midnight so the stored day matches the user's date
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        return date.toISOString();
      }
      
      // Try to parse as date and convert to ISO string
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      } catch {
        // If parsing fails, return the original value
      }
      
      return dateStr;
    };

    // Custom create/update handlers that convert foreign key strings back to numbers
    const customCreate = async (data: Partial<FormTask>, files?: File[], filesToDetach?: number[]) => {
      const processedData: Partial<Task> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        taskAssignedFrom: convertToNumber(data.taskAssignedFrom),
        taskAssignedTo: convertToNumber(data.taskAssignedTo),
        projectId: convertToNumber(data.projectId),
        supportTicketId: convertToNumber(data.supportTicketId),
        taskCategoryId: convertToNumber(data.taskCategoryId),
        taskTypeId: convertToNumber(data.taskTypeId),
        taskPriority: convertToNumber(data.taskPriority),
        taskStatus: convertToNumber(data.taskStatus),
        // Convert datetime strings (empty to undefined, date-only to ISO-8601)
        taskStartDate: convertDateTime(data.taskStartDate),
        taskEndDate: convertDateTime(data.taskEndDate),
        taskDate: convertDateToDateTime(data.taskDate), // Date input needs conversion to datetime
        taskEstimatedDate: convertDateToDateTime(data.taskEstimatedDate), // Date input needs conversion to datetime
      };
      return await moduleConfig.form.create(processedData, files, filesToDetach);
    };

    const customUpdate = async (id: number, data: Partial<FormTask>, files?: File[], filesToDetach?: number[]) => {
      const processedData: Partial<Task> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        taskAssignedFrom: convertToNumber(data.taskAssignedFrom),
        taskAssignedTo: convertToNumber(data.taskAssignedTo),
        projectId: convertToNumber(data.projectId),
        supportTicketId: convertToNumber(data.supportTicketId),
        taskCategoryId: convertToNumber(data.taskCategoryId),
        taskTypeId: convertToNumber(data.taskTypeId),
        taskPriority: convertToNumber(data.taskPriority),
        taskStatus: convertToNumber(data.taskStatus),
        // Convert datetime strings (empty to undefined, date-only to ISO-8601)
        taskStartDate: convertDateTime(data.taskStartDate),
        taskEndDate: convertDateTime(data.taskEndDate),
        taskDate: convertDateToDateTime(data.taskDate), // Date input needs conversion to datetime
        taskEstimatedDate: convertDateToDateTime(data.taskEstimatedDate), // Date input needs conversion to datetime
      };
      return await moduleConfig.form.update(id, processedData, files, filesToDetach);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Task>,
      mapItemToFormData: customMapItemToFormData as (item: Task) => Partial<Task>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Task>, files?: File[], filesToDetach?: number[]) => Promise<Task>,
        update: customUpdate as (id: number, data: Partial<Task>, files?: File[], filesToDetach?: number[]) => Promise<Task>,
      },
    };
  })();

  const configWithCalculateButton = {
    ...configWithOptions,
    fields: configWithOptions.fields.map((field) =>
      field.name === 'taskSpentMin'
        ? { ...field,  }
        : field
    ),
  };

  return (
    <GenericFormPage<Task>
      useData={useTask}
      config={configWithCalculateButton}
    />
  );
};

export default Form;

